import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { FaMobileAlt, FaLaptopCode, FaSmile, FaStar, FaFileAlt } from 'react-icons/fa';

const Stats = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const stats = [
    {
      icon: FaMobileAlt,
      number: 30,
      label: 'Apps Developed',
      delay: 0
    },
    {
      icon: FaLaptopCode,
      number: 30,
      label: 'Web Developed',
      delay: 0.1
    },
    {
      icon: FaSmile,
      number: 20,
      label: 'Happy Clients',
      delay: 0.2
    },
    {
      icon: FaStar,
      number: 50,
      label: 'Customer Ratings',
      delay: 0.3
    },
    {
      icon: FaFileAlt,
      number: 50,
      label: 'Projects Delivered',
      delay: 0.4
    }
  ];

  // Animated Counter Component
  const Counter = ({ end, duration = 2000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (inView) {
        let startTime;
        let animationFrame;

        const animate = (currentTime) => {
          if (!startTime) startTime = currentTime;
          const progress = (currentTime - startTime) / duration;

          if (progress < 1) {
            setCount(Math.floor(end * progress));
            animationFrame = requestAnimationFrame(animate);
          } else {
            setCount(end);
          }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame);
      }
    }, [inView, end, duration]);

    return <span>{count}</span>;
  };

  return (
    <section className="py-20 bg-white relative overflow-hidden" ref={ref}>
      {/* Animated Background Elements - Black & White */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gray-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-gray-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl lg:text-5xl font-rajdhani font-bold text-[#22446b] mb-3">
            Our Achievements
          </h2>
          <p className="text-gray-600 font-opensans text-lg">
            Numbers that speak for our excellence
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : { opacity: 0, y: 50, rotateX: -15 }}
              transition={{ 
                duration: 0.6, 
                delay: stat.delay,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                y: -10,
                rotateY: 5,
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              className="group relative"
              style={{ perspective: '1000px' }}
            >
              {/* 3D Card Container */}
              <div className="relative bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 transform-gpu border border-gray-100 group-hover:bg-[#106bcb]"
                style={{
                  transformStyle: 'preserve-3d',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.05)'
                }}
              >
                {/* Blue Border Glow Effect on Hover */}
                <div className="absolute inset-0 rounded-2xl bg-[#106bcb] opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10"></div>
                
                {/* Top Shine Effect */}
                <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/60 to-transparent rounded-t-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>

                {/* Icon Container with 3D Effect */}
                <div className="relative w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-hover:bg-white"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {/* Icon Shadow Layer */}
                  <div className="absolute inset-0 bg-[#106bcb] rounded-xl blur-md opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                    style={{ transform: 'translateZ(-10px)' }}
                  ></div>
                  
                  {/* Icon - Fixed to show properly */}
                  <stat.icon className="text-3xl text-[#106bcb] group-hover:text-[#22446b] relative z-10 transition-colors duration-500" />
                </div>

                {/* Number with 3D Text Effect */}
                <div className="relative mb-2">
                  <div className="text-5xl font-rajdhani font-black text-[#22446b] group-hover:text-white flex justify-center items-baseline gap-1 relative z-20 transition-colors duration-300">
                    <Counter end={stat.number} />
                    <span className="text-4xl">+</span>
                  </div>
                  {/* Text Shadow Layer - Hidden on hover to prevent visibility issues */}
                  <div className="absolute inset-0 text-5xl font-rajdhani font-black text-[#106bcb]/30 flex justify-center items-baseline gap-1 blur-sm opacity-20 group-hover:opacity-0 transition-opacity duration-300 pointer-events-none"
                    style={{ transform: 'translateZ(-5px) translateY(2px)' }}
                  >
                    {stat.number}
                    <span className="text-4xl">+</span>
                  </div>
                </div>

                {/* Label */}
                <div className="text-gray-600 text-sm font-opensans font-semibold text-center leading-tight group-hover:text-white transition-colors duration-300">
                  {stat.label}
                </div>

                {/* Bottom Accent Line - White on hover */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>

                {/* Corner Decorations - White on hover */}
                <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-gray-200 group-hover:bg-white transition-all duration-500"></div>
                <div className="absolute bottom-3 left-3 w-2 h-2 rounded-full bg-gray-200 group-hover:bg-white transition-all duration-500"></div>
              </div>

              {/* Floating Particles Effect - White */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-white rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
                <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-white rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping animation-delay-200"></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
      `}</style>
    </section>
  );
};

export default Stats;
